<template>
  <div>
    <el-popover
      ref="popover"
      v-if="calculatedData.length"
      placement="bottom"
      width="324"
      trigger="click"
      :close-delay="1"
    >
      <div class="recipients">
        <div class="title">
          {{ $t('CampaignForm_CalculateRecipients_Title') }}
        </div>
        <div v-if="!recipients.polling || !recipients.descriptiveCountTime">
          <div class="channels">
            <div v-for="(item, index) in calculatedData" :key="index">
              {{ $t(item.label) }}: {{ item.value >= 0 ? item.value : '?' }}
            </div>
          </div>
          <div class="sampling">
            <el-alert type="info" v-if="recipients.count.sampling_level">
              <i class="el-icon-info" />
              {{
                $t(
                  'CampaignForm_CalculateRecipients_Placeholder_Counting_Sampled'
                )
              }},
              <span class="link" @click="startCalculateExact">
                {{
                  $t(
                    'CampaignForm_CalculateRecipients_Placeholder_Counting_ClickHere'
                  )
                }}
              </span>
            </el-alert>
          </div>
        </div>
        <div class="placeholder" v-else>
          <div>
            {{ $t('CampaignForm_CalculateRecipients_Placeholder_Counting') }}
          </div>
          <div>
            {{
              $t('CampaignForm_CalculateRecipients_Placeholder_Count_Started', {
                time: `${new Date(
                  recipients.descriptiveCountTime * 1000
                ).toLocaleDateString('en-GB')} ${new Date(
                  recipients.descriptiveCountTime * 1000
                ).toLocaleTimeString('en-GB')}`
              })
            }}
          </div>
          <div>
            {{ $t('CampaignForm_CalculateRecipients_Placeholder_Check') }}
            <router-link :to="taskViewRoute" target="_blank">
              {{ $t('CampaignForm_CalculateRecipients_Placeholder_Link') }}
            </router-link>
          </div>
        </div>
        <div class="footer">
          <el-button
            :disabled="recipients.buttonsDisabled"
            type="text"
            @click="startCalculate"
          >
            <i class="el-icon-refresh"></i>
            {{ $t('CampaignForm_CalculateRecipients_Button') }}
          </el-button>
          <el-button
            :disabled="recipients.polling"
            type="text"
            @click="showList"
          >
            <i class="el-icon-view"></i>
            {{ $t('CampaignForm_CalculateRecipients_Button_Show') }}
          </el-button>
        </div>
      </div>
      <el-button
        :disabled="readonly"
        slot="reference"
        type="primary"
        plain
        @click="show"
      >
        <i class="el-icon-user-solid"></i>
        {{ $t('CampaignStepped_Btn_Calculate_Recipients') }}
      </el-button>
    </el-popover>
    <recipients-list-dialog
      v-if="recipients.showRecipientsList"
      :task-id="recipients.taskId"
      :dialog-visible.sync="recipients.showRecipientsList"
      :model="model"
    />
  </div>
</template>

<script>
import {
  channelLabels,
  channelTypes,
  messageChannelLabels,
  messageTypeLabels
} from '@/modules/campaign-manage/enum/Labels'
import { projectRoute } from '@/core/helpers'
import RecipientsListDialog from '@/modules/campaign-manage/components/RecipientsListDialog'
import { campaignTypeEnum } from '@/modules/campaigns/enum/CampaignTypeEnum'

export default {
  name: 'CalculateRecipients',
  components: { RecipientsListDialog },
  props: {
    model: {
      required: true
    },
    recipients: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      channelLabels,
      messageTypeLabels,
      messageChannelLabels
    }
  },
  computed: {
    calculatedData() {
      let selectedChannels = Object.keys(channelLabels)
        .filter(c => this.model[c])
        .map(c => channelTypes[c] || null)

      // override in-workflow enabled channels in workflow campaigns
      if (this.model.type === campaignTypeEnum.TYPE_WORKFLOW) {
        selectedChannels = [12]
      }

      if (this.model.promotion) {
        selectedChannels.push(13)
      }

      return [...new Set(selectedChannels)].reduce((result, ch) => {
        switch (ch) {
          case channelTypes.ios_push:
            if (this.model.ios_push_production) {
              result.push({
                label: messageChannelLabels[12],
                value: this.recipients.count.channel[12]
              })
            }
            if (this.model.ios_push_sandbox) {
              result.push({
                label: messageChannelLabels[13],
                value: this.recipients.count.channel[13]
              })
            }
            if (this.model.ios_push_legacy) {
              result.push({
                label: messageChannelLabels[14],
                value: this.recipients.count.channel[14]
              })
            }
            break
          case channelTypes.android_push:
            if (this.model.android_push_production) {
              result.push({
                label: messageChannelLabels[16],
                value: this.recipients.count.channel[16]
              })
            }
            if (this.model.android_push_legacy) {
              result.push({
                label: messageChannelLabels[17],
                value: this.recipients.count.channel[17]
              })
            }
            break
          case channelTypes.web_push:
            if (this.model.web_push_safari) {
              result.push({
                label: messageChannelLabels[19],
                value: this.recipients.count.channel[19]
              })
            }
            if (this.model.web_push_chrome) {
              result.push({
                label: messageChannelLabels[20],
                value: this.recipients.count.channel[20]
              })
            }
            if (this.model.web_push_firefox) {
              result.push({
                label: messageChannelLabels[21],
                value: this.recipients.count.channel[21]
              })
            }
            break
          case channelTypes.workflow:
            result.push({
              label: 'CampaignForm_CalculateRecipients_Channel_Available',
              value: this.recipients.count.message_type[ch]
            })
            break
          default:
            if (messageTypeLabels[ch]) {
              result.push({
                label: messageTypeLabels[ch],
                value: this.recipients.count.message_type[ch]
              })
            }
        }
        return result
      }, [])
    },
    taskViewRoute() {
      return projectRoute({
        name: 'TaskHistoryView',
        query: { id: this.recipients.taskId }
      })
    },
    readonly() {
      return this.$route.name === 'ViewCampaign'
    }
  },
  mounted() {
    this.$emit('check')
  },
  methods: {
    show() {
      this.$set(
        this.recipients,
        'count',
        this.recipients.count || {
          channel: {},
          message_type: {}
        }
      )
    },
    startCalculate() {
      this.$emit('calculate', true)
    },
    startCalculateExact() {
      this.$emit('calculate', false)
    },
    showList() {
      this.$emit('list')
    },
    togglePopper(toggle) {
      this.$refs['popover'].showPopper = toggle
    }
  },
  watch: {
    recipients: {
      handler: function() {
        if (this.recipients.showRecipientsList) {
          this.togglePopper(false)
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped lang="scss">
.recipients {
  margin: -12px;
  .title {
    padding: 16px 16px 8px;
    font-weight: 600;
    color: #4b515f;
  }
  .placeholder {
    padding: 0 16px 16px 16px;
    line-height: 22px;
    color: #7e8596;
    div: {
      word-break: break-word;
      text-align: left;
    }
  }
  .channels {
    padding: 0 16px 16px 16px;
    line-height: 22px;
    color: #7e8596;
    max-height: 125px;
    min-height: 44px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    div {
      min-width: 145px;
    }
  }
  .sampling {
    padding: 0 16px 16px 16px;
    .el-alert {
      word-break: break-word;
      text-align: left;
      i {
        margin-left: -10px;
        margin-bottom: 5px;
        margin-right: 5px;
      }
      .link {
        color: #4b515f;
        cursor: pointer;
      }
    }
  }
  .footer {
    background: #f8fafc;
    box-shadow: inset 0 1px 0 #ebeef5;
    button {
      padding: 16px;
      color: #1d87c8;
      font-size: 12px;
      font-weight: 600;
      i {
        font-weight: 600;
      }
      &.is-disabled {
        color: #c0c4cc;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/CalculateRecipients.vue